import { ListItem, ListItemContent } from '@/components/elements/lists';
import MerchantLogo from '@/components/elements/MerchantLogo/MerchantLogo';
import Icon from '@/components/icons/Icon';
import CardWrapper from '@/components/modules/CardWrapper';
import { _t } from '@/locale';
import { SelectExtraTimeProps } from './SelectExtraTime.types';

const baseTranslationKey = 'features.bookingSelection.SelectExtraTime';

/**
 * Presentational extra-time question for `/extra-tid`, ported from the modal's
 * `views/PickExtraTimeView.tsx` (left untouched — it stays the control arm) minus its
 * `Modal.Content` and `CheckoutViewLayout` wrappers, which the page shell now owns.
 *
 * The question renders as the page's `<h1>` so a guard redirect landing here announces it
 * (R15); both answers are native buttons via `ListItem`'s `onClick` rendering, so the view
 * is keyboard-operable with no extra wiring.
 *
 * No data fetching or navigation here — the page layer supplies the merchant logo and
 * reacts to `onAnswer` to store the requirement and advance the flow.
 */
const SelectExtraTime = ({ merchantLogoUrl, placeName, onAnswer }: SelectExtraTimeProps) => (
  <div className="flex flex-col items-center justify-center">
    <CardWrapper className="sm:!shadow-xs lg:!shadow-xs max-w-[600px] bg-inherit sm:!rounded-lg sm:bg-white">
      <div className="py-md sm:py-xl flex justify-center">
        <MerchantLogo imgUrl={merchantLogoUrl} alt={placeName ?? ''} size="lg" cornerRadius="rounded-lg" />
      </div>
      <div className="gap-4xl py-lg px-md sm:px-2xl flex flex-col">
        <h1 className="text-black-900 text-center text-[30px] font-bold sm:text-4xl">
          {_t(`${baseTranslationKey}.heading`)}
        </h1>
        <ul className="gap-md flex flex-col">
          <ListItem
            className="border-border_regular pl-lg rounded-md border bg-white sm:bg-inherit"
            onClick={() => onAnswer(true)}
            rightSlot={<Icon variant="arrow-right" />}
            leftPadding={false}>
            <ListItemContent
              title={_t(`${baseTranslationKey}.yes.title`)}
              content={_t(`${baseTranslationKey}.yes.subtitle`)}
              contentClassName="mt-[5px]"
            />
          </ListItem>
          <ListItem
            className="border-border_regular pl-lg rounded-md border bg-white sm:bg-inherit"
            onClick={() => onAnswer(false)}
            rightSlot={<Icon variant="arrow-right" />}
            leftPadding={false}>
            <ListItemContent
              title={_t(`${baseTranslationKey}.no.title`)}
              content={_t(`${baseTranslationKey}.no.subtitle`)}
              contentClassName="mt-[5px]"
            />
          </ListItem>
        </ul>
      </div>
    </CardWrapper>
  </div>
);

export default SelectExtraTime;
